import { normalizeAlternateEmail } from "@/utils/alternateEmail";
import { websiteValue } from "@/utils/website";

/**
 * Turns the parsed, schema-validated review form data into the request body
 * `confirmCandidateDetail` sends. The reverse of `mapApiToForm` in this same
 * folder. Kept in its own module so the one rule the whole alternate-email
 * feature hangs on — `alternate_email` lands as a top-level key, never
 * inside `personal_information` — is unit-testable without rendering the
 * form.
 */
export const buildConfirmPayload = (parsed) => {
  const { skills, personal_information, social, compensation = {}, experience, education, additionalSections, ...rest } = parsed;

  const { summary, name, linkedin, portfolio, github, alternate_email, ...personalRest } = personal_information;

  const n = (v) => (v === "" || v === undefined ? null : v);

  // Top-level languages: the paired-dropdown rows, cleaned. Empty proficiency
  // becomes null — a language without a level is valid and the server stores
  // it as such on applications.languages. Half rows are already refused by
  // the schema; this is belt-and-braces.
  const languageRows = (Array.isArray(skills.languages) ? skills.languages : [])
    .map((row) => ({
      language: String(row?.language ?? "").trim(),
      proficiency:
        row?.proficiency == null || String(row.proficiency).trim() === ""
          ? null
          : String(row.proficiency).trim(),
    }))
    .filter((row) => row.language);

  return {
    personal_information: {
      full_name: n(name),
      email: n(personalRest.email),
      phone: n(personalRest.phone),
      location: n(personalRest.location),
      linkedin: n(linkedin),
      website: n(websiteValue(portfolio)),
    },

    alternate_email: normalizeAlternateEmail(alternate_email),

    summary: n(summary),
    skills: skills.list ?? [],
    languages: languageRows,

    work_experience: experience.map((exp) => ({
      title: n(exp.jobTitle),
      company: n(exp.company),
      start_date: n(exp.startDate),
      end_date: n(exp.endDate),
      location: n(exp.location),
      description: n(exp.description),
      is_current: exp.endDate === "Present",
    })),

    education: education.map((edu) => ({
      institution: n(edu.institution),
      degree: n(edu.degree),
      field_of_study: n(edu.fieldOfStudy),
      start_date: n(edu.startDate),
      end_date: n(edu.endDate),
      grade: n(edu.grade),
    })),

    additional_sections: additionalSections.flatMap((section) =>
      section.items.map((item) => {
        const fields = item.reduce((acc, cur) => {
          if (cur.key?.trim()) acc[cur.key] = cur.value;
          return acc;
        }, {});
        return { section_title: section.section_title, fields };
      }),
    ),

    ...(social.instagram_url && { instagram_url: n(social.instagram_url) }),
    ...(social.facebook_url && { facebook_url: n(social.facebook_url) }),
    ...(social.twitter_url && { twitter_url: n(social.twitter_url) }),

    current_salary: n(compensation.current_salary),
    expected_salary: n(compensation.expected_salary),
    notice_period: n(compensation.notice_period),
    total_experience: compensation.total_experience || null,
  };
};
